// src/core/OidcAuthManager.ts
import { UserManager, WebStorageStateStore } from "oidc-client-ts";

// src/core/InMemoryTokenStore.ts
var InMemoryTokenStore = class {
  store = /* @__PURE__ */ new Map();
  async set(key, value) {
    this.store.set(key, value);
  }
  async get(key) {
    return this.store.get(key) ?? null;
  }
  async remove(key) {
    const value = this.store.get(key) ?? null;
    this.store.delete(key);
    return value;
  }
  async getAllKeys() {
    return Array.from(this.store.keys());
  }
};

// src/core/tokenLifecycle.ts
function shouldRenew(expiresAt, bufferSeconds = 30) {
  const nowSeconds = Math.floor(Date.now() / 1e3);
  return expiresAt - nowSeconds < bufferSeconds;
}
function createRenewalGuard() {
  let renewing = false;
  return {
    /** Returns true if the lock was acquired (caller should renew) */
    tryAcquire() {
      if (renewing) return false;
      renewing = true;
      return true;
    },
    /** Releases the renewal lock */
    release() {
      renewing = false;
    },
    /** Returns current lock state */
    isRenewing() {
      return renewing;
    }
  };
}

// src/core/OidcAuthManager.ts
function isInteractionRequired(err) {
  if (!err) return false;
  const msg = err instanceof Error ? err.message : String(err);
  return msg.includes("interaction_required") || msg.includes("login_required");
}
var TERMINAL_RENEWAL_ERRORS = ["invalid_grant", "login_required", "interaction_required", "consent_required"];
function isTerminalRenewalError(err) {
  if (!err) return false;
  const code = typeof err.error === "string" ? err.error : "";
  const msg = err instanceof Error ? err.message : String(err);
  return TERMINAL_RENEWAL_ERRORS.some((terminal) => code === terminal || msg.includes(terminal));
}
var TokenRenewalError = class extends Error {
  terminal;
  constructor(terminal) {
    super("Token renewal failed");
    this.name = "TokenRenewalError";
    this.terminal = terminal;
  }
};
var REAUTHENTICATION_REQUIRED = "Re-authentication required";
var DEFAULT_SCOPES = "openid profile email roles";
var DEFAULT_CALLBACK_PATH = "/auth/callback";
var DEFAULT_POST_LOGOUT_PATH = "/login";
var DEFAULT_SILENT_RENEW_PATH = "/auth/silent-renew.html";
var DEFAULT_EXPIRY_BUFFER_S = 30;
var DEFAULT_PROACTIVE_RENEW_LEAD_S = 120;
var PROACTIVE_RENEW_RETRY_MS = 15e3;
var DEFAULT_SESSION_CHECK_S = 30;
var E2E_AUTH_STORAGE_KEY = "smarttt.e2e.auth.v1";
var SSO_HANDOFF_PARAM = "sso_handoff";
var DEFAULT_RETURN_TO_KEY = "oidc.returnTo";
function consumeSsoHandoff() {
  if (typeof window === "undefined") return false;
  const url = new URL(window.location.href);
  if (url.searchParams.get(SSO_HANDOFF_PARAM) !== "1") return false;
  url.searchParams.delete(SSO_HANDOFF_PARAM);
  const returnTo = `${url.pathname}${url.search}${url.hash}`;
  try {
    window.sessionStorage.setItem(DEFAULT_RETURN_TO_KEY, returnTo);
  } catch {
  }
  window.history.replaceState(window.history.state, "", returnTo);
  return true;
}
var OidcAuthManager = class {
  userManager;
  config;
  renewalGuard = createRenewalGuard();
  state;
  listeners = /* @__PURE__ */ new Set();
  sessionRestoreAttempted = false;
  /** Shared promise for an in-flight token renewal so concurrent callers can await it. */
  renewalPromise = null;
  /** Timer for the manager-owned renewal ahead of access-token expiry (NEXT-3384). */
  proactiveRenewTimer = null;
  /** Redirect callbacks consume one-time state and must remain single-flight across remounts. */
  callbackPromise = null;
  /**
   * NEXT-3821: generation of the session that owns tokens. Bumped whenever that session ends
   * (logout, IdP sign-out, fenceSession(), re-authentication). A renewal started under an older
   * generation never installs its result.
   */
  sessionGeneration = 0;
  /** Generation captured by the renewal in flight; null when none is running. */
  renewalGeneration = null;
  /**
   * Token obtained by renewAfterRejection(). If the server rejects this token too,
   * authentication failure is persistent and the manager stops renewing (NEXT-3821).
   */
  tokenRenewedAfterRejection = null;
  /**
   * NEXT-522: Resolves when initialize() has settled (success OR failure).
   * Data-fetching layers (Apollo auth-link, GraphQL clients, RBAC-gated routes)
   * await this to avoid the bootstrap race where components fire requests
   * before signinSilent() has produced a token — which would otherwise leak
   * unauthenticated /graphql calls during the first ~1s after page load.
   */
  readyPromise;
  resolveReady;
  constructor(config) {
    this.config = config;
    this.state = { user: null, isLoading: true, isAuthenticated: false };
    this.readyPromise = new Promise((resolve) => {
      this.resolveReady = resolve;
    });
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    this.userManager = new UserManager({
      authority: config.authority,
      client_id: config.clientId,
      redirect_uri: `${origin}${config.callbackPath ?? DEFAULT_CALLBACK_PATH}`,
      post_logout_redirect_uri: `${origin}${config.postLogoutPath ?? DEFAULT_POST_LOGOUT_PATH}`,
      response_type: "code",
      scope: config.scopes ?? DEFAULT_SCOPES,
      // SECURITY: oidc-client-ts's internal automaticSilentRenew timer races
      // with OidcAuthManager.executeRenewal() (driven by AuthService's
      // PreemptiveRefreshScheduler). Two concurrent signinSilent() calls
      // both POST grant_type=refresh_token; Keycloak rotates single-use
      // refresh tokens, the loser is rejected, and oidc-client-ts clears
      // the user — including the winner's freshly-issued tokens.
      // executeRenewal()'s renewalGuard is the single renewal owner.
      automaticSilentRenew: false,
      silent_redirect_uri: `${origin}${config.silentRenewPath ?? DEFAULT_SILENT_RENEW_PATH}`,
      // SECURITY: Tokens stored in memory only — never in browser storage
      userStore: new InMemoryTokenStore(),
      // OIDC transaction state (PKCE state, nonce, code_verifier) uses sessionStorage
      stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
      filterProtocolClaims: true,
      loadUserInfo: false,
      monitorSession: false,
      checkSessionIntervalInSeconds: config.sessionCheckIntervalSeconds ?? DEFAULT_SESSION_CHECK_S,
      ...config.userManagerOverrides
    });
    this.bindEvents();
    this.bindLivenessListeners();
  }
  /**
   * Initializes the auth manager by attempting session restore.
   *
   * With in-memory token storage, getUser() returns null on fresh page load.
   * We attempt signinSilent() to restore the session via the Keycloak
   * httpOnly session cookie (sent automatically by the browser to the iframe).
   *
   * Must be called once after construction (typically in the React provider's useEffect).
   */
  async initialize() {
    const startMs = performance.now();
    const ssoHandoff = consumeSsoHandoff();
    this.emitLog("INFO", "auth.initialize.started");
    try {
      const callbackPath = this.config.callbackPath ?? DEFAULT_CALLBACK_PATH;
      if (typeof window !== "undefined" && window.location.pathname === callbackPath) {
        this.emitLog("INFO", "auth.initialize.skipped_on_callback", {
          callbackPath
        });
        return;
      }
      const e2eUser = this.readE2EUser();
      if (e2eUser) {
        this.setState({
          user: e2eUser.user,
          isLoading: false,
          isAuthenticated: true
        });
        const durationMs = Math.round(performance.now() - startMs);
        this.emitLog("INFO", "auth.session.restored", {
          sub: e2eUser.user.id,
          expiresAt: e2eUser.expiresAt,
          durationMs,
          source: "e2e-bootstrap"
        });
        return;
      }
      const existingUser = await this.userManager.getUser();
      if (existingUser && this.installUser(existingUser, this.sessionGeneration, null, "initialize")) {
        this.setState({ ...this.state, isLoading: false });
        return;
      }
      if (!this.sessionRestoreAttempted) {
        this.sessionRestoreAttempted = true;
        try {
          const generation = this.sessionGeneration;
          const restored = await this.userManager.signinSilent();
          if (restored && this.installUser(restored, generation, null, "session_restore")) {
            const durationMs = Math.round(performance.now() - startMs);
            this.emitLog("INFO", "auth.session.restored", {
              sub: restored.profile?.sub,
              expiresAt: restored.expires_at,
              durationMs
            });
          } else {
            const durationMs = Math.round(performance.now() - startMs);
            this.emitLog("INFO", "auth.session.restore_failed", { durationMs });
          }
        } catch (e) {
          const durationMs = Math.round(performance.now() - startMs);
          if ((!isInteractionRequired(e) || ssoHandoff) && (this.config.ssoRedirectFallback ?? true)) {
            this.emitLog("INFO", "auth.sso.redirect_fallback", { durationMs, ssoHandoff });
            await this.userManager.signinRedirect({ prompt: "none" });
            return;
          }
          this.emitLog("INFO", "auth.session.restore_failed", { durationMs });
        }
      }
    } finally {
      this.setState({ ...this.state, isLoading: false });
      const durationMs = Math.round(performance.now() - startMs);
      const sub = this.state.user?.id;
      this.emitLog("INFO", "auth.initialize.completed", { durationMs, ...sub ? { sub } : {} });
      this.resolveReady();
    }
  }
  /**
   * NEXT-522: Returns a promise that resolves when {@link initialize} has settled
   * (either restored a session or determined that none is restorable). Downstream
   * data-fetching layers MUST await this before issuing any authenticated request,
   * otherwise they will leak unauthenticated requests during the ~1s bootstrap
   * window after page load.
   *
   * If `initialize()` is never called, this never resolves. Safe to await
   * multiple times.
   */
  whenReady() {
    return this.readyPromise;
  }
  /** Subscribe to auth state changes. Returns an unsubscribe function. */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  /** Get the current auth state snapshot. */
  getState() {
    return this.state;
  }
  /** Trigger OIDC redirect login. */
  async login() {
    if (this.readE2EUser()) {
      return;
    }
    await this.userManager.signinRedirect();
  }
  /** Trigger OIDC logout redirect. */
  async logout() {
    this.endSessionGeneration();
    if (this.clearE2EUser()) {
      this.setState({ user: null, isLoading: false, isAuthenticated: false });
      return;
    }
    this.setState({ user: null, isLoading: false, isAuthenticated: false });
    await this.userManager.signoutRedirect();
  }
  /**
   * Ends the current session generation without an IdP round trip (NEXT-3821) — for a tenant
   * switch or any other change that makes the current principal's tokens inapplicable. Any
   * renewal still in flight completes into a discarded result; the state is signed out.
   */
  fenceSession(reason) {
    this.endSessionGeneration();
    this.emitLog("INFO", "auth.session.fenced", { reason });
    this.setState({ user: null, isLoading: false, isAuthenticated: false });
  }
  /**
   * Bounded recovery after the server rejected an access token as unauthenticated (HTTP 401)
   * (NEXT-3821). Never call this for 403: an authorization denial is not an authentication
   * failure and renewing cannot change it.
   *
   * - If the stored token is no longer the rejected one (a concurrent renewal already replaced
   *   it), the replacement is returned without renewing again — a 401 burst costs one renewal.
   * - Otherwise one single-flight renewal runs; concurrent callers share it.
   * - If the token being rejected is itself the product of an earlier rejection-driven
   *   renewal, authentication failure is persistent: the session ends with
   *   reauthenticationRequired instead of renewing again.
   * - Any renewal failure ends the session the same way.
   *
   * Resolves to the token a caller may use for one retry of a safe read. The caller must never
   * replay a mutation on the strength of this result.
   */
  async renewAfterRejection(rejectedAccessToken) {
    if (this.readE2EUser()) {
      throw new Error(REAUTHENTICATION_REQUIRED);
    }
    if (this.state.reauthenticationRequired) {
      throw new Error(REAUTHENTICATION_REQUIRED);
    }
    const generation = this.sessionGeneration;
    const current = await this.userManager.getUser();
    if (generation !== this.sessionGeneration || !current) {
      throw new Error("User is not authenticated");
    }
    if (rejectedAccessToken && current.access_token !== rejectedAccessToken && !this.isExpired(current)) {
      return current.access_token;
    }
    if (rejectedAccessToken && rejectedAccessToken === this.tokenRenewedAfterRejection) {
      this.signOutForReauthentication("persistent_authentication_failure");
      throw new Error(REAUTHENTICATION_REQUIRED);
    }
    let token;
    try {
      token = await this.renewSingleFlight();
    } catch {
      if (generation === this.sessionGeneration) {
        this.signOutForReauthentication("renewal_failed_after_rejection");
      }
      throw new Error(REAUTHENTICATION_REQUIRED);
    }
    if (!token) {
      if (generation === this.sessionGeneration) {
        this.signOutForReauthentication("no_session_after_rejection");
      }
      throw new Error(REAUTHENTICATION_REQUIRED);
    }
    this.tokenRenewedAfterRejection = token;
    return token;
  }
  /**
   * Returns a valid access token.
   *
   * A token inside the expiry buffer, or already expired, is renewed first: the refresh token
   * outlives the access token, so an expired access token is a reason to renew, not proof that
   * the session is gone (NEXT-3384). Renewal is single-flight; concurrent callers share it.
   * Throws when there is no user, or when renewal fails or yields no user.
   */
  async getAccessToken() {
    const e2eUser = this.readE2EUser();
    if (e2eUser) {
      return e2eUser.accessToken;
    }
    if (this.state.reauthenticationRequired) {
      throw new Error(REAUTHENTICATION_REQUIRED);
    }
    const generation = this.sessionGeneration;
    const oidcUser = await this.userManager.getUser();
    if (!oidcUser) {
      throw new Error("User is not authenticated");
    }
    const bufferS = this.config.expiryBufferSeconds ?? DEFAULT_EXPIRY_BUFFER_S;
    if (oidcUser.expired || shouldRenew(oidcUser.expires_at ?? 0, bufferS)) {
      let token;
      try {
        token = await this.renewSingleFlight();
      } catch (err) {
        if (generation === this.sessionGeneration) {
          this.handleRenewalFailure(err);
        }
        throw err;
      }
      if (token) return token;
      if (generation !== this.sessionGeneration) {
        throw new Error("User is not authenticated");
      }
      this.signOutForReauthentication("no_session");
      throw new Error("Session expired \u2014 silent renewal returned no user");
    }
    return oidcUser.access_token;
  }
  /**
   * Runs the one renewal in flight, or joins it. Resolves to the new access token, or null when
   * signinSilent() produced no user. Rejects with "Token renewal failed" when renewal threw.
   */
  renewSingleFlight() {
    if (this.renewalGuard.tryAcquire()) {
      const promise = this.executeRenewal();
      this.renewalPromise = promise;
      const clear = () => {
        if (this.renewalPromise === promise) this.renewalPromise = null;
      };
      promise.then(clear, clear);
      return promise;
    }
    if (this.renewalPromise) {
      return this.renewalPromise;
    }
    return Promise.reject(new Error("Token renewal failed"));
  }
  /**
   * Performs the actual silent renewal, releases the guard when done,
   * and returns the new access token (or null if signinSilent returned null).
   */
  async executeRenewal() {
    const startMs = performance.now();
    const generation = this.sessionGeneration;
    const owner = this.state.user;
    const expectedIdentity = owner ? { id: owner.id, tenantId: owner.tenantId } : null;
    this.renewalGeneration = generation;
    let renewed;
    try {
      renewed = await this.userManager.signinSilent();
    } catch (err) {
      throw new TokenRenewalError(isTerminalRenewalError(err));
    } finally {
      this.renewalGeneration = null;
      this.renewalGuard.release();
    }
    if (!renewed) {
      return null;
    }
    if (generation !== this.sessionGeneration) {
      this.emitLog("WARN", "auth.token.install_refused", { reason: "superseded_session", source: "renewal" });
      return null;
    }
    if (!this.installUser(renewed, generation, expectedIdentity, "renewal")) {
      throw new TokenRenewalError(false);
    }
    const durationMs = Math.round(performance.now() - startMs);
    this.emitLog("INFO", "auth.silent_renew.success", {
      durationMs,
      expiresAt: renewed.expires_at
    });
    return renewed.access_token;
  }
  /**
   * Decides whether a failed renewal ends the session (NEXT-3821). A terminal IdP answer, or a
   * failure while the stored token has already expired, leaves nothing usable: the session ends
   * with reauthenticationRequired instead of looping on renewals. A non-terminal failure while
   * the token is still valid keeps the session; the proactive retry tries again before expiry.
   */
  handleRenewalFailure(err) {
    const terminal = err instanceof TokenRenewalError ? err.terminal : isTerminalRenewalError(err);
    const user = this.state.user;
    const expired = !user || user.expiresAt <= Math.floor(Date.now() / 1e3);
    if (terminal || expired) {
      this.signOutForReauthentication(terminal ? "renewal_rejected" : "renewal_failed_after_expiry");
    }
  }
  /** Ends the session after an unrecoverable authentication failure (NEXT-3821). */
  signOutForReauthentication(reason) {
    if (this.state.reauthenticationRequired && !this.state.user) {
      return;
    }
    this.endSessionGeneration();
    Promise.resolve().then(() => this.userManager.removeUser()).catch(() => void 0);
    this.emitLog("WARN", "auth.session.reauthentication_required", { reason });
    this.setState({ user: null, isLoading: false, isAuthenticated: false, reauthenticationRequired: true });
  }
  endSessionGeneration() {
    this.sessionGeneration += 1;
    this.tokenRenewedAfterRejection = null;
  }
  isExpired(oidcUser) {
    const expiresAt = oidcUser.expires_at;
    return typeof expiresAt !== "number" || !Number.isFinite(expiresAt) || expiresAt <= Math.floor(Date.now() / 1e3);
  }
  /**
   * The single installation point for tokens (NEXT-3821). A token is made active only when it
   * belongs to the current session generation, has not already expired (oidc-client-ts's own
   * expiry rule: expires_at in the past — no extra validity is assumed), and, for a renewal,
   * carries the same principal and tenant as the session it renews.
   *
   * @returns true when the user was installed
   */
  installUser(oidcUser, generation, expectedIdentity, source) {
    if (generation !== this.sessionGeneration) {
      this.emitLog("WARN", "auth.token.install_refused", { reason: "superseded_session", source });
      return false;
    }
    if (this.isExpired(oidcUser)) {
      this.emitLog("WARN", "auth.token.install_refused", {
        reason: "expired",
        source,
        expiresAt: oidcUser.expires_at,
        now: Math.floor(Date.now() / 1e3)
      });
      return false;
    }
    if (expectedIdentity && oidcUser.profile) {
      let candidate;
      try {
        candidate = this.config.claimMapper(
          oidcUser.profile,
          oidcUser.access_token,
          oidcUser.expires_at ?? 0
        );
      } catch {
        candidate = null;
      }
      if (!candidate || candidate.id !== expectedIdentity.id || candidate.tenantId !== expectedIdentity.tenantId) {
        this.emitLog("WARN", "auth.token.install_refused", { reason: "principal_changed", source });
        return false;
      }
    }
    this.updateUser(oidcUser);
    return true;
  }
  /**
   * Schedules the manager's own renewal ahead of expiry (NEXT-3384).
   *
   * Renewal used to happen only when a caller asked for a token inside the last
   * expiryBufferSeconds. A console whose live data arrives over a WebSocket makes few HTTP
   * calls, so with a 300 s token that window was usually missed; the token then expired, the
   * old getAccessToken() refused to renew an expired token, and every later request went out
   * without a credential while token issuance itself stayed healthy. Renewing on a timer keeps
   * getState().user.accessToken current for synchronous readers too.
   *
   * The lead is capped at half the token's remaining lifetime so a short-lived token is not
   * renewed immediately on every refresh.
   */
  scheduleProactiveRenewal(expiresAtSeconds) {
    this.cancelProactiveRenewal();
    if (!expiresAtSeconds) return;
    const remainingMs = expiresAtSeconds * 1e3 - Date.now();
    const leadMs = Math.min(
      (this.config.proactiveRenewLeadSeconds ?? DEFAULT_PROACTIVE_RENEW_LEAD_S) * 1e3,
      Math.max(0, remainingMs / 2)
    );
    this.armProactiveRenewal(Math.max(0, remainingMs - leadMs));
  }
  armProactiveRenewal(delayMs) {
    this.cancelProactiveRenewal();
    this.proactiveRenewTimer = setTimeout(() => {
      this.proactiveRenewTimer = null;
      void this.runProactiveRenewal();
    }, delayMs);
  }
  async runProactiveRenewal() {
    if (!this.state.user) return;
    try {
      const token = await this.renewSingleFlight();
      if (!token) {
        this.emitLog("WARN", "auth.proactive_renew.no_user");
      }
    } catch (err) {
      const expiresAt = this.state.user?.expiresAt ?? 0;
      this.emitLog("WARN", "auth.proactive_renew.failure", {
        reason: err instanceof Error ? err.message : String(err),
        expiresAt
      });
      this.handleRenewalFailure(err);
      if (this.state.user) {
        this.armProactiveRenewal(PROACTIVE_RENEW_RETRY_MS);
      }
    }
  }
  cancelProactiveRenewal() {
    if (this.proactiveRenewTimer !== null) {
      clearTimeout(this.proactiveRenewTimer);
      this.proactiveRenewTimer = null;
    }
  }
  /**
   * NEXT-3384: browsers throttle a hidden tab's timers — Chrome/Firefox drop a
   * background setTimeout to roughly once a minute after the tab has been hidden for
   * ~5 minutes. proactiveRenewTimer is armed for a precise delay, so on a long-hidden
   * tab it can fire tens of minutes late, well past expiry. Re-check on the signals
   * that mean the tab is live again — becoming visible, regaining focus, or coming
   * back online — and renew immediately if due, instead of waiting for the throttled
   * timer to eventually catch up.
   */
  bindLivenessListeners() {
    if (typeof document === "undefined") return;
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("focus", this.onWindowFocus);
    window.addEventListener("online", this.onWindowOnline);
  }
  unbindLivenessListeners() {
    if (typeof document === "undefined") return;
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("focus", this.onWindowFocus);
    window.removeEventListener("online", this.onWindowOnline);
  }
  onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      this.reviveRenewalIfDue();
    }
  };
  onWindowFocus = () => {
    this.reviveRenewalIfDue();
  };
  onWindowOnline = () => {
    this.reviveRenewalIfDue();
  };
  /**
   * Renews immediately when a user is present and the current token is already inside
   * the proactive lead (or expired) — the condition a throttled timer would otherwise
   * have missed. Delegates to runProactiveRenewal() so success/failure handling
   * (rescheduling via updateUser(), or a 15 s retry on failure) stays in one place.
   */
  reviveRenewalIfDue() {
    if (this.readE2EUser()) return;
    const user = this.state.user;
    if (!user) return;
    const leadS = this.config.proactiveRenewLeadSeconds ?? DEFAULT_PROACTIVE_RENEW_LEAD_S;
    const nowS = Math.floor(Date.now() / 1e3);
    const dueForRenewal = user.expiresAt - nowS <= leadS;
    if (!dueForRenewal) return;
    void this.runProactiveRenewal();
  }
  /** Processes the OIDC redirect callback (code → token exchange). */
  handleCallback() {
    if (this.readE2EUser()) {
      return Promise.resolve();
    }
    if (!this.callbackPromise) {
      this.callbackPromise = this.processCallback();
    }
    return this.callbackPromise;
  }
  /** Executes the one-time OIDC transaction consumed by {@link handleCallback}. */
  async processCallback() {
    try {
      const generation = this.sessionGeneration;
      const oidcUser = await this.userManager.signinRedirectCallback();
      if (oidcUser) {
        if (!this.installUser(oidcUser, generation, null, "callback")) {
          this.signOutForReauthentication("callback_token_unusable");
          return;
        }
        this.emitLog("INFO", "auth.callback.success", { sub: oidcUser.profile?.sub });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (isInteractionRequired(err)) {
        this.emitLog("INFO", "auth.sso.interaction_required", { reason: message });
        await this.userManager.signinRedirect();
        return;
      }
      this.emitLog("ERROR", "auth.callback.failure", { error: message });
      throw err;
    }
  }
  /** Access the underlying UserManager (for WebSocket connectionParams). */
  getUserManager() {
    return this.userManager;
  }
  /** Clean up event listeners. Call on unmount. */
  dispose() {
    this.cancelProactiveRenewal();
    this.userManager.events.removeUserLoaded(this.onUserLoaded);
    this.userManager.events.removeSilentRenewError(this.onSilentRenewError);
    this.userManager.events.removeUserSignedOut(this.onUserSignedOut);
    this.userManager.events.removeUserSessionChanged(this.onUserSessionChanged);
    this.unbindLivenessListeners();
    this.listeners.clear();
  }
  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------
  bindEvents() {
    this.userManager.events.addUserLoaded(this.onUserLoaded);
    this.userManager.events.addSilentRenewError(this.onSilentRenewError);
    this.userManager.events.addUserSignedOut(this.onUserSignedOut);
    this.userManager.events.addUserSessionChanged(this.onUserSessionChanged);
  }
  onUserLoaded = (oidcUser) => {
    this.emitLog("INFO", "auth.user.loaded", {
      sub: oidcUser.profile?.sub,
      expiresAt: oidcUser.expires_at,
      scope: oidcUser.scope
    });
    if (this.renewalGeneration !== null) return;
    this.installUser(oidcUser, this.sessionGeneration, null, "user_loaded");
  };
  onSilentRenewError = (error) => {
    this.emitLog("ERROR", "auth.silent_renew.failure", {
      error: error?.message ?? "unknown error"
    });
    this.setState({ user: null, isLoading: false, isAuthenticated: false });
    this.userManager.signinRedirect().catch((_err) => {
    });
  };
  onUserSignedOut = () => {
    this.emitLog("INFO", "auth.user.signed_out");
    this.endSessionGeneration();
    this.setState({ user: null, isLoading: false, isAuthenticated: false });
  };
  onUserSessionChanged = () => {
    this.userManager.getUser().then((oidcUser) => {
      if (oidcUser && !oidcUser.expired) {
        this.emitLog("INFO", "auth.session.changed", { sessionValid: true });
        this.updateUser(oidcUser);
      } else {
        this.emitLog("INFO", "auth.session.changed", { sessionValid: false });
        this.setState({ user: null, isLoading: false, isAuthenticated: false });
      }
    }).catch((err) => {
      this.emitLog("WARN", "auth.session.changed.error", { reason: err instanceof Error ? err.message : String(err) });
      this.setState({ user: null, isLoading: false, isAuthenticated: false });
    });
  };
  updateUser(oidcUser) {
    const profile = oidcUser.profile;
    if (!profile) {
      this.setState({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }
    const user = this.config.claimMapper(
      profile,
      oidcUser.access_token,
      oidcUser.expires_at ?? 0
    );
    this.setState({
      user,
      isLoading: false,
      isAuthenticated: user !== null
    });
    if (user) {
      this.scheduleProactiveRenewal(oidcUser.expires_at ?? 0);
    }
  }
  setState(newState) {
    if (!newState.user) {
      this.cancelProactiveRenewal();
    }
    this.state = newState;
    for (const listener of this.listeners) {
      listener(newState);
    }
  }
  readE2EUser() {
    if (typeof window === "undefined") {
      return null;
    }
    const hostname = window.location.hostname;
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
    if (!isLocalHost) {
      return null;
    }
    const raw = window.localStorage.getItem(E2E_AUTH_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      if (!parsed.profile || typeof parsed.accessToken !== "string") {
        return null;
      }
      const expiresAt = typeof parsed.expiresAt === "number" ? parsed.expiresAt : Math.floor(Date.now() / 1e3) + 3600;
      const user = this.config.claimMapper(
        parsed.profile,
        parsed.accessToken,
        expiresAt
      );
      if (!user) {
        return null;
      }
      return {
        user,
        accessToken: parsed.accessToken,
        expiresAt
      };
    } catch {
      return null;
    }
  }
  clearE2EUser() {
    if (typeof window === "undefined") {
      return false;
    }
    const raw = window.localStorage.getItem(E2E_AUTH_STORAGE_KEY);
    if (!raw) {
      return false;
    }
    window.localStorage.removeItem(E2E_AUTH_STORAGE_KEY);
    return true;
  }
  /**
   * Emits a structured JSON log entry to the appropriate console channel.
   *
   * Every entry includes: timestamp, level, event, component, clientId.
   * Additional domain-specific fields are spread from the optional `fields` argument.
   */
  emitLog(level, event, fields) {
    const entry = {
      timestamp: (/* @__PURE__ */ new Date()).toISOString(),
      level,
      event,
      component: "OidcAuthManager",
      clientId: this.config.clientId,
      ...fields
    };
    if (level === "ERROR") console.error(JSON.stringify(entry));
    else if (level === "WARN") console.warn(JSON.stringify(entry));
    else console.info(JSON.stringify(entry));
  }
};

// src/core/claimExtractor.ts
function extractTenantId(profile) {
  if (typeof profile["tenantId"] === "string" && profile["tenantId"] !== "") {
    return profile["tenantId"];
  }
  if (typeof profile["tenant_id"] === "string" && profile["tenant_id"] !== "") {
    return profile["tenant_id"];
  }
  return "";
}
function extractRoles(profile) {
  if (Array.isArray(profile["roles"])) {
    return profile["roles"];
  }
  return [];
}
function extractStringClaim(profile, claimName, fallback = "") {
  const value = profile[claimName];
  return typeof value === "string" ? value : fallback;
}

// src/core/correlationId.ts
function buildCorrelationId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `corr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// src/core/rbac.ts
function hasRole(user, role) {
  if (!user) return false;
  return user.allRoles.includes(role);
}
function hasAnyRole(user, roles) {
  if (!user || roles.length === 0) return false;
  return roles.some((role) => user.allRoles.includes(role));
}
function hasAllRoles(user, roles) {
  if (!user || roles.length === 0) return false;
  return roles.every((role) => user.allRoles.includes(role));
}

export {
  InMemoryTokenStore,
  shouldRenew,
  createRenewalGuard,
  OidcAuthManager,
  extractTenantId,
  extractRoles,
  extractStringClaim,
  buildCorrelationId,
  hasRole,
  hasAnyRole,
  hasAllRoles
};
//# sourceMappingURL=chunk-GL2JF7ZX.js.map